import { useState, useEffect, useRef } from 'react';
import {
  socketService,
  ConnectionStatus,
  WsProgressUpdate,
  WsHelpRequest,
  WsHelpResolved,
  WsHelpMessage,
  WsSessionReaction,
  WsMessageReaction,
  WsSessionChatMessage,
} from '../services/socket.service';
import { useAuth } from './useAuth';

interface UseSessionSocketOptions {
  sessionId: string;
  onProgressUpdate?: (data: WsProgressUpdate) => void;
  onHelpRequest?: (data: WsHelpRequest) => void;
  onHelpResolved?: (data: WsHelpResolved) => void;
  onHelpMessage?: (data: WsHelpMessage) => void;
  onBroadcast?: (data: { sessionId: string; message: string }) => void;
  onSessionReaction?: (data: WsSessionReaction) => void;
  onMessageReaction?: (data: WsMessageReaction) => void;
  onSessionChatMessage?: (data: WsSessionChatMessage) => void;
  onSessionEnded?: (data: { sessionId: string }) => void;
  onSessionPaused?: (data: { sessionId: string }) => void;
  onSessionResumed?: (data: { sessionId: string }) => void;
}

export function useSessionSocket({
  sessionId,
  onProgressUpdate,
  onHelpRequest,
  onHelpResolved,
  onHelpMessage,
  onBroadcast,
  onSessionReaction,
  onMessageReaction,
  onSessionChatMessage,
  onSessionEnded,
  onSessionPaused,
  onSessionResumed,
}: UseSessionSocketOptions) {
  const { token } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');

  // Store callbacks in refs to avoid re-subscribing on every render
  const onProgressRef = useRef(onProgressUpdate);
  onProgressRef.current = onProgressUpdate;
  const onHelpRequestRef = useRef(onHelpRequest);
  onHelpRequestRef.current = onHelpRequest;
  const onHelpResolvedRef = useRef(onHelpResolved);
  onHelpResolvedRef.current = onHelpResolved;
  const onHelpMessageRef = useRef(onHelpMessage);
  onHelpMessageRef.current = onHelpMessage;
  const onBroadcastRef = useRef(onBroadcast);
  onBroadcastRef.current = onBroadcast;
  const onSessionReactionRef = useRef(onSessionReaction);
  onSessionReactionRef.current = onSessionReaction;
  const onMessageReactionRef = useRef(onMessageReaction);
  onMessageReactionRef.current = onMessageReaction;
  const onSessionChatRef = useRef(onSessionChatMessage);
  onSessionChatRef.current = onSessionChatMessage;
  const onSessionEndedRef = useRef(onSessionEnded);
  onSessionEndedRef.current = onSessionEnded;
  const onSessionPausedRef = useRef(onSessionPaused);
  onSessionPausedRef.current = onSessionPaused;
  const onSessionResumedRef = useRef(onSessionResumed);
  onSessionResumedRef.current = onSessionResumed;

  useEffect(() => {
    if (!token || !sessionId) return;

    // Connect and join room
    socketService.connect(token);
    socketService.joinSession(sessionId);

    // Status listener
    const unsubStatus = socketService.onStatusChange((status) => {
      setConnectionStatus(status);
      // Re-join room on reconnect
      if (status === 'connected') {
        socketService.joinSession(sessionId);
      }
    });

    // Set initial status
    setConnectionStatus(socketService.status);

    // Event subscriptions
    const unsubProgress = socketService.onProgressUpdate((data) => {
      onProgressRef.current?.(data);
    });

    const unsubHelpRequest = socketService.onHelpRequest((data) => {
      onHelpRequestRef.current?.(data);
    });

    const unsubHelpResolved = socketService.onHelpResolved((data) => {
      onHelpResolvedRef.current?.(data);
    });

    const unsubHelpMessage = socketService.onHelpMessage((data) => {
      onHelpMessageRef.current?.(data);
    });

    const unsubBroadcast = socketService.onSessionBroadcast((data) => {
      onBroadcastRef.current?.(data);
    });

    const unsubSessionReaction = socketService.onSessionReaction((data) => {
      onSessionReactionRef.current?.(data);
    });

    const unsubMessageReaction = socketService.onMessageReaction((data) => {
      onMessageReactionRef.current?.(data);
    });

    const unsubSessionChat = socketService.onSessionChatMessage((data) => {
      onSessionChatRef.current?.(data);
    });

    const unsubSessionEnded = socketService.onSessionEnded((data) => {
      onSessionEndedRef.current?.(data);
    });

    const unsubSessionPaused = socketService.onSessionPaused((data) => {
      onSessionPausedRef.current?.(data);
    });

    const unsubSessionResumed = socketService.onSessionResumed((data) => {
      onSessionResumedRef.current?.(data);
    });

    return () => {
      unsubStatus();
      unsubProgress();
      unsubHelpRequest();
      unsubHelpResolved();
      unsubHelpMessage();
      unsubBroadcast();
      unsubSessionReaction();
      unsubMessageReaction();
      unsubSessionChat();
      unsubSessionEnded();
      unsubSessionPaused();
      unsubSessionResumed();
      socketService.leaveSession(sessionId);
      socketService.disconnect();
    };
  }, [token, sessionId]);

  const isConnected = connectionStatus === 'connected';

  return { connectionStatus, isConnected };
}
